import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { GlobalOutlined, ThunderboltOutlined } from '@ant-design/icons'
import cn from 'classnames'
import { AccountContext } from 'core/context'
import { useLanguage } from 'context/LanguageContext'
import { PublicReportService } from 'api/http'
import {
    CryptoCalculatorDataModel,
    FuturesCalculatorDataModel,
    PublicRobotsModel,
} from 'api/http/models'
import { EXCHANGES, NAV_LINK_NAMES } from 'shared/consts'
import { CALCULATOR_ANCHOR } from 'shared/components/LiveRobotCalculator/anchor'
import { RECOMMENDED_DEPOSIT_RATIO } from 'shared/components/LiveRobotCalculator/calc'
import {
    CheckYourself,
    HistoryFacts,
    Knobs,
} from 'shared/components/LiveRobotCalculator/Facts'
import { GetStarted } from '../GetStarted'
import translations from './RobotShowcase.translations'
import s from './RobotShowcase.module.scss'

export type Market = 'global' | 'crypto'
type CryptoExchange = 'bybit' | 'okx' | 'bitget' | 'binance'

/** Криптобиржи: подпись и страница торговли (там полный калькулятор). */
const CRYPTO_EXCHANGES: { key: CryptoExchange; label: string; route: string }[] = [
    { key: 'bybit', label: 'Bybit', route: NAV_LINK_NAMES.TRADING_BYBIT },
    { key: 'okx', label: 'OKX', route: NAV_LINK_NAMES.TRADING_OKX },
    { key: 'bitget', label: 'Bitget', route: NAV_LINK_NAMES.TRADING_BITGET },
    { key: 'binance', label: 'Binance', route: NAV_LINK_NAMES.TRADING_BINANCE },
]

/** Депозит фьючерсов по умолчанию, если у инструмента не задан шаг (ES — 6 млн на лот). */
export const FUTURES_DEFAULT_DEPOSIT = 6_000_000

type Item =
    | { market: 'global'; key: string; data: FuturesCalculatorDataModel }
    | {
          market: 'crypto'
          key: string
          data: CryptoCalculatorDataModel
          exchange: CryptoExchange
      }

const defaultDeposit = (item: Item) =>
    item.market === 'global'
        ? item.data.compoundStep || item.data.minBalance || FUTURES_DEFAULT_DEPOSIT
        : Math.round((item.data.minBalance || 5000) * RECOMMENDED_DEPOSIT_RATIO)

/** BTCUSDT → BTC, LITUSDT-10000 → LIT. */
export const coinOf = (symbol: string) => symbol.replace(/-\d+$/, '').replace(/USDT$/, '')

interface Props {
    /** Какой рынок показывать; без него (главная) — оба, с переключателем. */
    market?: Market
}

/**
 * Витрина робота для посетителя: факты торговли (сделки, контракты,
 * прибыль по дням — из базы) и «проверьте сами» — ползунки, которые стоят
 * на фактах, а посетитель двигает их и видит доход.  На главной оба рынка
 * с переключателем, на страницах рынков — свой.
 */
const Showcase: React.FC<Props> = React.memo(({ market }) => {
    const { language } = useLanguage()
    const t = translations[language] ?? translations.en
    const { accountData } = useContext(AccountContext)

    const [robots, setRobots] = useState<PublicRobotsModel>()
    const [active, setActive] = useState<Market>(market ?? 'global')
    const [selected, setSelected] = useState<string>()
    const [knobs, setKnobs] = useState<Knobs>({})

    useEffect(() => {
        PublicReportService.getRobots()
            .then(setRobots)
            .catch(() => setRobots({ rithmic: [], bybit: [], okx: [], bitget: [], binance: [] }))
    }, [])

    // Только инструменты с историей сделок; сначала — где сделок больше.
    const byMarket = useMemo(() => {
        const ready = (i: Item) => !!i.data.history
        const order = (a: Item, b: Item) => (b.data.history?.deals ?? 0) - (a.data.history?.deals ?? 0)
        const global: Item[] = (robots?.rithmic ?? [])
            .map(data => ({ market: 'global' as const, key: `g-${data.symbolId}`, data }))
            .filter(ready)
            .sort(order)
        const crypto: Item[] = CRYPTO_EXCHANGES.flatMap(({ key: exchange }) =>
            (robots?.[exchange] ?? []).map(data => ({
                market: 'crypto' as const,
                key: `${exchange}-${data.symbolId}`,
                data,
                exchange,
            }))
        )
            .filter(ready)
            .sort(order)
        return { global, crypto }
    }, [robots])

    const current = market ?? active
    const items = byMarket[current]
    const item = items.find(i => i.key === selected) ?? items[0]

    if (!robots) return null
    // На главной блок нужен, если есть хоть один рынок; на странице рынка — свой.
    if (market ? !items.length : !byMarket.global.length && !byMarket.crypto.length) return null

    const switchTo = (next: Market) => {
        setActive(next)
        setSelected(undefined)
        setKnobs({})
    }

    const crypto = item?.market === 'crypto'
    const cryptoExchange =
        item?.market === 'crypto' ? CRYPTO_EXCHANGES.find(e => e.key === item.exchange) : undefined
    const route = cryptoExchange ? cryptoExchange.route : NAV_LINK_NAMES.TRADING_RITHMIC
    const link = item ? `${route}?symbol=${item.data.symbolId}#${CALCULATOR_ANCHOR}` : route
    const titleId = `robot-showcase-${market ?? 'main'}`
    const currency = item?.market === 'global' ? item.data.currency : 'USDT'
    const coin = item?.market === 'crypto' ? coinOf(item.data.symbol) : ''

    return (
        <section className={cn(s.wrapper, market && s.embedded)} aria-labelledby={titleId}>
            <div className={s.header}>
                <h2 id={titleId}>
                    {market === 'global' ? t.titleGlobal : market === 'crypto' ? t.titleCrypto : t.title}
                </h2>
                <p>{t.subtitle}</p>
            </div>

            {!market && (
                <div className={s.switch} role="tablist" aria-label={t.title}>
                    {(['global', 'crypto'] as Market[]).map(m => (
                        <button
                            key={m}
                            type="button"
                            role="tab"
                            aria-selected={current === m}
                            className={cn(s.switchCard, current === m && s.switchActive)}
                            disabled={!byMarket[m].length}
                            onClick={() => switchTo(m)}
                        >
                            <span className={s.switchIcon}>
                                {m === 'global' ? <GlobalOutlined /> : <ThunderboltOutlined />}
                            </span>
                            <span className={s.switchText}>
                                <b>{m === 'global' ? t.globalName : t.cryptoName}</b>
                                <span>{m === 'global' ? t.globalWhat : t.cryptoWhat}</span>
                            </span>
                            <span className={s.switchLogos} aria-hidden>
                                {EXCHANGES[m].slice(0, 4).map(el => (
                                    <img key={el.href} src={el.icon} alt="" />
                                ))}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {item?.data.history && (
                <>
                    {items.length > 1 && (
                        <div className={s.tabs} role="tablist">
                            {items.map(i => (
                                <button
                                    key={i.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={i.key === item.key}
                                    className={cn(s.tab, i.key === item.key && s.tabActive)}
                                    onClick={() => {
                                        setSelected(i.key)
                                        setKnobs({})
                                    }}
                                >
                                    {i.market === 'crypto'
                                        ? `${CRYPTO_EXCHANGES.find(e => e.key === i.exchange)?.label} · ${i.data.name}`
                                        : i.data.name}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className={s.grid}>
                        <HistoryFacts
                            history={item.data.history}
                            riskMetrics={item.data.riskMetrics}
                            crypto={crypto}
                            coin={coin}
                            positionSize={item.data.positionSize}
                            currency={currency}
                            instrument={`${cryptoExchange ? cryptoExchange.label : 'CME · Rithmic'} · ${item.data.name}`}
                        />
                        <CheckYourself
                            data={item.data}
                            crypto={crypto}
                            coin={coin}
                            currency={currency}
                            knobs={knobs}
                            onChange={setKnobs}
                            defaultDeposit={defaultDeposit(item)}
                            idPrefix={titleId}
                            footer={
                                accountData?.id ? (
                                    <Link className={s.cta} to={link}>
                                        {t.openCalculator} →
                                    </Link>
                                ) : (
                                    <div className={s.guest}>
                                        <GetStarted type="primary" />
                                        <span>{t.guestNote}</span>
                                    </div>
                                )
                            }
                        />
                    </div>
                </>
            )}
            <p className={s.disclaimer}>{t.disclaimer}</p>
        </section>
    )
})

/** Сбой витрины (неожиданные данные API) не должен ронять страницу — блок просто скрывается. */
class SilentBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
    state = { failed: false }

    static getDerivedStateFromError() {
        return { failed: true }
    }

    componentDidCatch(error: Error) {
        console.error('RobotShowcase:', error)
    }

    render() {
        return this.state.failed ? null : this.props.children
    }
}

export const RobotShowcase: React.FC<Props> = props => (
    <SilentBoundary>
        <Showcase {...props} />
    </SilentBoundary>
)
